import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  getIssueTypes,
  getItems,
  getServiceRequestTags,
} from 'src/services/client';

export interface IssueType {
  id: string;
  name: string;
}

interface ServiceRequestTag {
  id: string;
  name: string;
}

export interface UseServiceRequestFormOptionsReturn {
  types: IssueType[];
  serviceRequestTags: ServiceRequestTag[];
  items: any[];
  loading: boolean;
  error: string | null;
  fetchFormOptions: (userId?: string, role?: string) => Promise<void>;
}

const parseListResponse = async (response: any, label: string): Promise<any[]> => {
  if (response.ok || response.status === 200) {
    return (await response.json()) || [];
  }

  console.error(`Failed to fetch ${label}`);
  return [];
};

export const useServiceRequestFormOptions = (): UseServiceRequestFormOptionsReturn => {
  const [types, setTypes] = useState<IssueType[]>([]);
  const [serviceRequestTags, setServiceRequestTags] = useState<ServiceRequestTag[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFormOptions = async (
    userId?: string,
    role?: string
  ): Promise<void> => {
    setLoading(true);
    setError(null);

    const shouldFetchItems: boolean = Boolean(userId && role);

    try {
      const requests: Promise<any>[] = [getIssueTypes(), getServiceRequestTags()];

      if (shouldFetchItems) {
        requests.push(getItems(userId, role));
      }

      const [typesResponse, tagsResponse, itemsResponse]: any[] =
        await Promise.all(requests);

      const typesData: IssueType[] = await parseListResponse(
        typesResponse,
        'issue types'
      );
      const tagsData: ServiceRequestTag[] = await parseListResponse(
        tagsResponse,
        'service request tags'
      );

      setTypes(typesData);
      setServiceRequestTags(tagsData);

      if (shouldFetchItems) {
        const itemsData: any[] = await parseListResponse(
          itemsResponse,
          'catalog items'
        );
        setItems(itemsData);
      }
    } catch (err: any) {
      console.error('Error fetching form options:', err);

      setError('Failed to load form options');
      toast.error('🚫 Failed to load form options');
    } finally {
      setLoading(false);
    }
  };

  return { types, serviceRequestTags, items, loading, error, fetchFormOptions };
};